<template>
  <BaseScreen v-bind="screen">
    <BaseAlert
      v-if="isErrorVisible"
      v-bind="error"
      slot="middle"
    />
    <Recorder
      v-else
      ref="recorder"
      slot="middle"
      :audio="audio"
      @start="onRecordStart"
      @stop="onRecordStop"
      @abort="confirmAbort"
      @audio="onAudioReady"
      @continue="goNext"
      @error="showError"
    />
    <ModalConfirm
      v-if="isAbortModalVisible"
      v-bind="abortModal"
      slot="root"
      class="screen__overlay"
      @agree="abortRecording"
      @disagree="hideAbortModal"
    />
  </BaseScreen>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

import screenMixin from '@/mixins/screen';
import ModalConfirm from '@/components/ModalConfirm.vue';
import Recorder from '@/components/Recorder.vue';

import CONSTANTS from '@/helpers/constants';

const {
  STEP_AUDIO_RECORD_IS_READY,
  STEP_MODAL_CONFIRM_AUDIO_ABORT,
} = CONSTANTS;

export default {
  name: 'TakeAudioScreen',
  mixins: [screenMixin],
  components: {
    ModalConfirm,
    Recorder,
  },
  data() {
    return {
      isRecording: false,
      isAbortModalVisible: false,
      isAudioProcessing: false,
    };
  },
  staticData: () => ({
    abortPath: '',
  }),
  computed: {
    ...mapState('feedback', [
      'audio',
    ]),
    pendingText() {
      return this.isAudioProcessing
        ? this.$t('pending.processing')
        : this.$t('pending.hardware');
    },
    error() {
      return {
        title: this.$t('titles.badHardware'),
        text: this.$t('texts.feedbackNotPossible'),
        actions: [
          {
            text: this.$t('actions.home'),
            action: () => this.$router.push(this.$router.options.paths.home),
          },
        ],
      };
    },
    abortModal() {
      return {
        title: this.$t('titles.confirmAbortRecording'),
        agreeText: this.$t('actions.yesAbort'),
        disagreeText: this.$t('actions.noContinue'),
        replic: STEP_MODAL_CONFIRM_AUDIO_ABORT,
      };
    },
    isAudioReady() {
      return Boolean(this.audio);
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.isRecording) {
      this.confirmAbort(to.path);
    } else {
      next();
    }
  },
  mounted() {
    this.startRecording();
  },
  methods: {
    ...mapMutations('feedback', [
      'setAudio',
    ]),
    confirmAbort(path) {
      this.abortPath = path || this.$router.options.paths.home;
      this.showAbortModal();
    },
    startRecording() {
      return this.$refs.recorder.start();
    },
    stopRecording() {
      return this.$refs.recorder.stop();
    },
    onRecordStart() {
      this.isRecording = true;
      this.hidePending();
    },
    onRecordStop() {
      this.isRecording = false;
      this.hidePending();
      this.showAudioProcessing();
    },
    onAudioReady(src) {
      this.setAudio(src);
      this.hideAudioProcessing();
      this.sayAudioReady();
    },
    sayAudioReady() {
      this.$robot.dialogService.setCase(STEP_AUDIO_RECORD_IS_READY, true, true);
      this.$robot.dialogService.sayReplicByName(STEP_AUDIO_RECORD_IS_READY, true);
    },
    abortRecording() {
      this.stopRecording();
      this.$router.push(this.abortPath);
    },
    showAudioProcessing() {
      this.isAudioProcessing = true;
      this.showPending();
    },
    hideAudioProcessing() {
      this.isAudioProcessing = false;
      this.hidePending();
    },
    showAbortModal() {
      this.isAbortModalVisible = true;
    },
    hideAbortModal() {
      this.isAbortModalVisible = false;
    },
    goNext() {
      if (!this.isAudioReady) return;
      this.$router.push(this.$router.options.paths.sendAudio);
    },
  },
};
</script>
